import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  useTheme,
  Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import {
  formatDate,
  formatDateWithoutTime,
} from "../../../../../views/components/common/functions/formatDate";
import { StockIssueRequestStore } from "../../../stores/StockIssueRequestStore";
import { SampleSirActivityEntry } from "../../../../../models/StockIssueRequest/StockIssueRequest";
import {
  getModernPageColors,
  modernDrawerHeaderSx,
  modernEmptyStateSx,
} from "../../../../../styles/modernListingPageSx";

interface StockIssueRequestActivityLogsDrawerProps {
  open: boolean;
  onClose: () => void;
  sirId: number | null;
  store: StockIssueRequestStore;
}

const ACTIVITY_LOGS_DRAWER_Z_OFFSET = 2000;

const AVATAR_COLORS = [
  "#1976d2",
  "#388e3c",
  "#d32f2f",
  "#7b1fa2",
  "#f57c00",
  "#0288d1",
  "#689f38",
  "#e64a19",
];

const getInitials = (name: string | null): string => {
  const trimmed = name?.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(" ").filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return trimmed[0].toUpperCase();
};

const getAvatarColor = (name: string | null): string => {
  const trimmed = name?.trim() || "?";
  let hash = 0;
  for (let i = 0; i < trimmed.length; i++) {
    hash = trimmed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

/**
 * API returns oldest → newest. Show newest first by reversing that list so
 * same-timestamp events keep the API sequence (e.g. print inward then rejection SIR).
 */
const reverseApiOrderNewestFirst = (
  logs: SampleSirActivityEntry[],
): SampleSirActivityEntry[] => [...logs].reverse();

const groupLogsByDate = (
  logs: SampleSirActivityEntry[],
): Record<string, SampleSirActivityEntry[]> => {
  const grouped: Record<string, SampleSirActivityEntry[]> = {};
  reverseApiOrderNewestFirst(logs).forEach((log) => {
    const dateKey = log.occurred_at
      ? formatDateWithoutTime(log.occurred_at, {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "Unknown date";
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push(log);
  });
  return grouped;
};

const ActivityLogEntry: React.FC<{ log: SampleSirActivityEntry }> = ({
  log,
}) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);

  return (
    <Box sx={{ display: "flex", gap: 1.25 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.7rem",
          fontWeight: 700,
          color: "#fff",
          backgroundColor: getAvatarColor(log.admin_name),
        }}
      >
        {getInitials(log.admin_name)}
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          px: 1.5,
          py: 1.25,
          backgroundColor: colors.surface,
        }}
      >
        <CustomTypography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
          {log.label}
        </CustomTypography>
        <CustomTypography
          variant="caption"
          sx={{ color: colors.subtitle, fontSize: "0.72rem" }}
        >
          {log.occurred_at ? formatDate(log.occurred_at) : "—"}
          {log.admin_name ? ` · ${log.admin_name}` : ""}
        </CustomTypography>
      </Box>
    </Box>
  );
};

const StockIssueRequestActivityLogsDrawer: React.FC<
  StockIssueRequestActivityLogsDrawerProps
> = ({ open, onClose, sirId, store }) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);

  useEffect(() => {
    if (open && sirId) {
      store.getStockIssueRequestActivityLogs(sirId).catch(() => {});
    }
    if (!open) {
      store.resetActivityLogs();
    }
  }, [open, sirId, store]);

  const groupedLogs = groupLogsByDate(store.activityLogs);
  const sortedDates = Object.keys(groupedLogs).sort((a, b) => {
    const aTime = groupedLogs[a][0]?.occurred_at
      ? new Date(groupedLogs[a][0].occurred_at!).getTime()
      : 0;
    const bTime = groupedLogs[b][0]?.occurred_at
      ? new Date(groupedLogs[b][0].occurred_at!).getTime()
      : 0;
    return bTime - aTime;
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420, md: 480 },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: (muiTheme) =>
            muiTheme.zIndex.drawer + ACTIVITY_LOGS_DRAWER_Z_OFFSET,
        },
        slotProps: {
          backdrop: {
            sx: {
              zIndex: (muiTheme) =>
                muiTheme.zIndex.drawer + ACTIVITY_LOGS_DRAWER_Z_OFFSET - 1,
            },
          },
        },
      }}
    >
      <Box sx={modernDrawerHeaderSx(theme)}>
        <Box display="flex" gap={1.5} alignItems="center" minWidth={0}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              backgroundColor: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <HistoryIcon sx={{ color: "white", fontSize: 22 }} />
          </Box>
          <Box minWidth={0}>
            <CustomTypography variant="heading5" sx={{ color: colors.title }}>
              Activity Logs
            </CustomTypography>
            <CustomTypography
              variant="body2"
              sx={{
                mt: 0.5,
                color: colors.subtitle,
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              {sirId ? `Stock Issue Request #${sirId}` : "SIR history"}
            </CustomTypography>
          </Box>
        </Box>

        <IconButton onClick={onClose} size="small" aria-label="Close activity logs">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
        }}
      >
        {store.activityLogsLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 240,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : store.activityLogs.length === 0 ? (
          <Box sx={{ ...modernEmptyStateSx(theme), minHeight: 240 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <HistoryIcon sx={{ fontSize: 44, opacity: 0.5 }} />
              <CustomTypography variant="body1">
                No activity recorded for this stock issue request yet.
              </CustomTypography>
            </Box>
          </Box>
        ) : (
          sortedDates.map((date) => (
            <Box key={date} sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Box sx={{ height: "1px", flex: 1, backgroundColor: colors.border }} />
                <CustomTypography
                  variant="body2"
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    backgroundColor: colors.headerBg,
                    borderRadius: "12px",
                    color: colors.subtitle,
                  }}
                >
                  {date}
                </CustomTypography>
                <Box sx={{ height: "1px", flex: 1, backgroundColor: colors.border }} />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {groupedLogs[date].map((log, index) => (
                  <ActivityLogEntry
                    key={`${log.event_type}-${log.occurred_at}-${index}`}
                    log={log}
                  />
                ))}
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Drawer>
  );
};

export default observer(StockIssueRequestActivityLogsDrawer);
